import { Component, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { ComponentsModule, DialogService, SnackBarService, TabAppearance, TabItem } from '@mastechnicaegroup/carelinelive-ui';
import { CommonModule } from '@angular/common';
import { catchError, map, of, switchMap, take, tap } from 'rxjs';
import { RefreshableResult, successfulResult, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { CommissionerService } from '@lib/commissioner/commissioner.service';
import { EditCommissionerGeneralInformationDialogComponent } from '../../dialogs/commissioner/edit/general-information/edit-commissioner-general-information-dialog.component';
import { EditCommissionerWorkingHoursComponent } from '../../dialogs/commissioner/edit/working-hours/edit-commissioner-working-hours.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { CommissionerUsersTabComponent } from './tabs/users/commissioner-users-tab.component';
import { CommissionerAuditTabComponent } from './tabs/audit/commissioner-audit-tab.component';
import { CommissionerWorkingHoursComponent } from './accordion/working-hours/commissioner-working-hours.component';
import { CommissionerContactAddressComponent } from './accordion/contact-address/commissioner-contact-address.component';
import { CommissionerGeneralInformationComponent } from './accordion/general-information/commissioner-general-information.component';
import { EditCommissionerContactAddressDialogComponent } from '../../dialogs/commissioner/edit/contact-address/edit-commissioner-contact-address-dialog.component';
import { CommissionerPackagesTabComponent } from './tabs/packages/commissioner-packages-tab.component';
import { Commissioner, CommissionerSingleResponse, StoreCommissionerRequest } from '@lib/api/models/commissioner';
import { PromptCommissionerStatusChangeDialogComponent } from '../../dialogs/commissioner/prompt/prompt-commissioner-status-change-dialog.component';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { EditCommissionerUserPositionDialogComponent } from '../../dialogs/commissioner/edit/user-position/edit-commissioner-user-position-dialog.component';
import { CommissionerUser } from '@lib/api/models/commissioner-users';

@Component({
    selector: 'app-commissioners-view-page',
    imports: [
        CommonModule,
        ComponentsModule,
        ReactiveFormsModule,
        HeaderComponent,
        CommissionerUsersTabComponent,
        CommissionerPackagesTabComponent,
        CommissionerAuditTabComponent,
        CommissionerGeneralInformationComponent,
        CommissionerContactAddressComponent,
        CommissionerWorkingHoursComponent,
        ErrorPanelComponent
    ],
    templateUrl: './commissioners-view-page.component.html',
    standalone: true,
    styleUrl: './commissioners-view-page.component.scss'
})
export class CommissionersViewPageComponent {

    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _authService = inject(AuthenticationService);
    private readonly _commissionerService = inject(CommissionerService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _dialog = inject(DialogService);
    private readonly _snackBar = inject(SnackBarService);
    protected currentTabId = '0';

    protected user = toSignal(this._authService.user$);

    protected isUsersTabOpen = true;
    protected isPackagesTabOpen = false;
    protected isAuditTabOpen = false;

    tabAppearance: TabAppearance = 'contained';
    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '0',
            title: 'Users'
        },
        {
            disabled: false,
            id: '1',
            title: 'Packages'
        },
        {
            disabled: false,
            id: '2',
            title: 'Audit'
        },
    ];

    commissioner = new RefreshableResult({
        source: this._activatedRoute.params.pipe(
            map(({ id }) => id),
            switchMap(id => this._commissionerService.getCommissioner(id).pipe(
                toResult()
            ))
        )
    });

    commissioner$ = this.commissioner.result$.pipe(
        successfulResult()
    );

    private readonly _id$ = this.commissioner$.pipe(
        map((commissioner) => commissioner?.data.id)
    );

    private addCommissionerUserRequest = new SendableRequest<CommissionerSingleResponse, StoreCommissionerRequest>({
        request: (payload) => {
            return this._id$.pipe(
                take(1),
                switchMap((id) => {
                    return this._commissionerService.updateCommissioner(id!, payload!);
                })
            );
        },

        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Commissioner user added successfully.'
            });

            this.commissioner.refresh();
        }
    });

    private removeCommissionerUserRequest = new SendableRequest<CommissionerSingleResponse, StoreCommissionerRequest>({
        request: (payload) => {
            return this._id$.pipe(
                take(1),
                switchMap((id) => {
                    return this._commissionerService.updateCommissioner(id!, payload!);
                })
            );
        },

        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Commissioner user removed successfully.'
            });

            this.commissioner.refresh();
        }
    });

    editContactAddress() {
        this.commissioner$.pipe(
            take(1),
            switchMap((commissionerData) => {
                return EditCommissionerContactAddressDialogComponent.show(this._dialog, commissionerData.data).closed.pipe(
                    tap(() => {
                        this.commissioner.refresh();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to edit contact address.'
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    editGeneralInformation() {
        this.commissioner$.pipe(
            take(1),
            switchMap((commissionerData) => {
                return EditCommissionerGeneralInformationDialogComponent.show(this._dialog, commissionerData.data).closed.pipe(
                    tap(() => {
                        this.commissioner.refresh();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to edit general information.'
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    editWorkingHours() {
        this.commissioner$.pipe(
            take(1),
            switchMap((commissionerData) => {
                return EditCommissionerWorkingHoursComponent.show(this._dialog, commissionerData.data).closed.pipe(
                    tap(() => {
                        this.commissioner.refresh();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to edit working hours.'
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    openUsersTab() {
        this.closeAllTabs();
        this.isUsersTabOpen = true;
    }

    openPackagesTab() {
        this.closeAllTabs();
        this.isPackagesTabOpen = true;
    }

    openAuditTab() {
        this.closeAllTabs();
        this.isAuditTabOpen = true;
    }

    closeAllTabs() {
        this.isUsersTabOpen = false;
        this.isPackagesTabOpen = false;
        this.isAuditTabOpen = false;
    }

    changeStatus(commissioner: Commissioner, status: boolean) {

        const commissionerData = {
            ...commissioner,
            status: status
        };

        return PromptCommissionerStatusChangeDialogComponent.show(this._dialog, commissionerData).closed.pipe(
            tap(() => {
                this.commissioner.refresh();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Commissioner status failed to change',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });
                return of(null);
            })
        ).subscribe();
    }

    public currentTabIdChange(tabId: string) {
        this.currentTabId = tabId;
        switch (tabId) {
            case '0':
                this.openUsersTab();
                break;
            case '1':
                this.openPackagesTab();
                break;
            case '2':
                this.openAuditTab();
                break;
        }
    }

    addUserEvent($event : {user_id: string, role: string}) {
        const commissionerData = this.commissioner$.pipe(
            take(1),
            map((commissioner) => ({
                reference: commissioner.data.reference,
                name: commissioner.data.name,
                email: commissioner.data.email,
                users_to_store: [$event]
            }))
        );

        commissionerData.subscribe(data => {
            this.addCommissionerUserRequest.send(data);
        });
    }

    removeUserEvent($event: string) {
        const commissionerData = this.commissioner$.pipe(
            take(1),
            map((commissioner) => ({
                reference: commissioner.data.reference,
                name: commissioner.data.name,
                email: commissioner.data.email,
                users_to_remove: [{user_id: $event}]
            }))
        );

        commissionerData.subscribe(data => {
            this.removeCommissionerUserRequest.send(data);
        });
    }

    editCommissionerUser(commissionerUserData: CommissionerUser) {
        this.commissioner$.pipe(
            take(1),
            switchMap((commissionerResult) => {
                const commissionerReference = commissionerResult?.data.reference;

                const updatedCommissionerUserData = {
                    ...commissionerUserData,
                    commissionerReference: commissionerReference
                };

                return EditCommissionerUserPositionDialogComponent.show(this._dialog, updatedCommissionerUserData).closed.pipe(
                    tap(() => {
                        this.commissioner.refresh();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            icon: 'error',
                            title: 'Failed to edit commissioner user.'
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

}
